import React from "react";
import { graphql, useStaticQuery } from "gatsby";

export default function Seo({ seoData = {}, children }) {
  const query = useStaticQuery(graphql`
    query seoStaticQuery {
      allSite {
        nodes {
          siteMetadata {
            siteUrl
          }
        }
      }
    }
  `);

  const siteUrl = query?.allSite?.nodes?.[0]?.siteMetadata?.siteUrl;

  const seo = {
    ...seoData,
    combinedTitle: seoData.seo?.titleTemplate
      ? seoData.seo.titleTemplate
          .replace("%n", seoData.siteName)
          .replace("%s", seoData.title)
      : seoData.title,
    robots: [
      seoData.seo?.noindex ? "noindex" : "index",
      seoData.seo?.noindex || seoData.nofollow ? "nofollow" : "follow",
    ],
  };

  const metaTags = [
    {
      name: "og:type",
      content: "website",
      isProperty: true,
      isRepeatable: false,
    },
  ];
  if (seo.title) {
    metaTags.push({
      name: "og:title",
      content: seo.title,
      isProperty: true,
      isRepeatable: false,
    });
  }
  if (seo.description) {
    metaTags.push({
      name: "og:description",
      content: seo.description,
      isProperty: true,
      isRepeatable: false,
    });
  }
  if (seo.url) {
    metaTags.push({
      name: "og:url",
      content: seo.url,
      isProperty: true,
      isRepeatable: false,
    });
  }
  if (seo.siteName) {
    metaTags.push({
      name: "og:site_name",
      content: seo.siteName,
      isProperty: true,
      isRepeatable: false,
    });
  }
  if (seo.locale) {
    metaTags.push({
      name: "og:locale",
      content: convertLocale(seo.locale),
      isProperty: true,
      isRepeatable: false,
    });
  }

  return (
    <>
      <title>{seo.combinedTitle}</title>
      <meta name="robots" content={seo.robots} />
      <meta name="googlebot" content={seo.robots} />
      {getAlternateLinks(seo.alternates)}
      {!seo.description ? (
        <></>
      ) : (
        <meta name="description" content={seo.description} />
      )}
      {getPreviewMediaTags(seo.previewMedia, siteUrl)}
      {getTwitterTags(seo.twitter)}
      {getFacebookTags(seo.facebook)}
      {getMetaTags(mergeMetaTags(metaTags, seoData.seo?.metaTags))}

      {children}
    </>
  );
}

function convertLocale(locale) {
  if (!locale) {
    return locale;
  }
  const localeSplits = locale.split("-");
  if (localeSplits.length === 2) {
    localeSplits[1] = localeSplits[1].toUpperCase();
  }
  return localeSplits.join("_");
}

function mergeMetaTags(...metaTags) {
  return metaTags.filter(Boolean).reduce((merged, current) => {
    current.filter(Boolean).forEach((metaTag) => {
      const existingMetaTag = merged.find(
        (mergedMetaTag) =>
          mergedMetaTag.isProperty === metaTag.isProperty &&
          mergedMetaTag.name === metaTag.name
      );
      if (
        existingMetaTag &&
        (!metaTag.isRepeatable || !existingMetaTag.isRepeatable)
      ) {
        existingMetaTag.content = metaTag.content;
      } else {
        merged.push(metaTag);
      }
    });
    return merged;
  }, []);
}

function getAlternateLinks(alternates) {
  if (!alternates?.length) {
    return <></>;
  }

  if (
    alternates.some(
      ({ fields, ...alternate }) =>
        !fields ||
        fields.some((field) => !alternate[field]) ||
        Object.keys(alternate).some((key) => !fields.includes(key))
    )
  ) {
    console.warn("Incorrect defined seo alternates.\n", alternates);
  }

  return (
    alternates
      .filter(
        ({ fields, ...alternate }) =>
          fields &&
          !fields.some((field) => !alternate[field]) &&
          !Object.keys(alternate).some((key) => !fields.includes(key))
      )
      // eslint-disable-next-line no-unused-vars
      .map(({ fields, ...alternate }, idx) => {
        return (
          <link
            rel="alternate"
            key={`alternate-${alternate.hrefLang || alternate.media || "unknown"}-${idx}`}
            {...alternate}
          />
        );
      })
  );
}

function getPreviewMediaTags(previewMedia, siteUrl) {
  if (!previewMedia?.length) {
    return <></>;
  }
  const supportedImageExts = [
    ".jpg",
    ".jpeg",
    ".png",
    ".svg",
    ".webp",
    ".avif",
  ];
  const supportedVideoExts = [".mp4"];

  if (
    previewMedia.some(
      (media) =>
        (!media.ext ||
          (!supportedImageExts.includes(media.ext) &&
            !supportedVideoExts.includes(media.ext))) &&
        !media.image
    )
  ) {
    console.warn("Unsupported seo preview media.\n", previewMedia);
  }

  const previewMediaTags = previewMedia
    .filter(
      (media) =>
        (media.ext &&
          (supportedImageExts.includes(media.ext) ||
            supportedVideoExts.includes(media.ext))) ||
        media.image
    )
    .map((media) =>
      media.ext
        ? supportedImageExts.includes(media.ext)
          ? getPreviewImageTags(media, siteUrl)
          : getPreviewVideoTags(media, siteUrl)
        : getPreviewImageShopifyTags(media, siteUrl)
    )
    .flat();

  if (!previewMediaTags.length) {
    return <></>;
  }
  return previewMediaTags.map((tag, idx) => (
    <meta
      key={`image-tag-${idx}`}
      property={tag.property}
      content={tag.content}
    />
  ));
}

function getPreviewImageTags(previewImage, siteUrl) {
  const alt = previewImage.alternativeText || previewImage.name || "";
  const url =
    previewImage.file?.childImageSharp?.gatsbyImageData?.images?.sources?.[0]?.srcSet.match(
      /w,\n(.+) \d+w$/
    )?.[1];
  const type =
    previewImage.file?.childImageSharp?.gatsbyImageData?.images?.sources?.[0]
      ?.type;
  const fallback =
    previewImage.file?.childImageSharp?.gatsbyImageData?.images?.fallback
      ?.src || previewImage.url;

  const fallbackType = previewImage.mime;
  const height =
    previewImage.file?.childImageSharp?.gatsbyImageData?.height ||
    previewImage.height;
  const width =
    previewImage.file?.childImageSharp?.gatsbyImageData?.width ||
    previewImage.width;

  const imageTags = [
    { property: "og:image", content: getAbsoluteUrl(url, siteUrl) },
    { property: "og:image:alt", content: alt },
    { property: "og:image:type", content: type },
    { property: "og:image:height", content: height },
    { property: "og:image:width", content: width },
  ];

  const fallbackTags = [
    { property: "og:image", content: getAbsoluteUrl(fallback, siteUrl) },
    { property: "og:image:alt", content: alt },
    { property: "og:image:type", content: fallbackType },
    { property: "og:image:height", content: height },
    { property: "og:image:width", content: width },
  ];

  if (url) {
    if (url !== fallback) {
      return [...imageTags, ...fallbackTags];
    } else {
      return imageTags;
    }
  } else {
    return fallbackTags;
  }
}

function getPreviewImageShopifyTags(previewImage, siteUrl) {
  const alt = previewImage.image.altText || previewImage.alt || "";
  const url =
    previewImage.image.gatsbyImageData?.images?.sources?.[0]?.srcSet.match(
      /w,\n(.+) \d+w$/
    )?.[1];
  const type = previewImage.image.gatsbyImageData?.images?.sources?.[0]?.type;
  const fallback =
    previewImage.image.gatsbyImageData?.images?.fallback?.src ||
    previewImage.image.src;

  const height =
    previewImage.image.gatsbyImageData?.height || previewImage.image.height;
  const width =
    previewImage.image.gatsbyImageData?.width || previewImage.image.width;

  const imageTags = [
    { property: "og:image", content: getAbsoluteUrl(url, siteUrl) },
    { property: "og:image:alt", content: alt },
    { property: "og:image:type", content: type },
    { property: "og:image:height", content: height },
    { property: "og:image:width", content: width },
  ];

  const fallbackTags = [
    { property: "og:image", content: getAbsoluteUrl(fallback, siteUrl) },
    { property: "og:image:alt", content: alt },
    { property: "og:image:height", content: height },
    { property: "og:image:width", content: width },
  ];

  if (url) {
    if (url !== fallback) {
      return [...imageTags, ...fallbackTags];
    } else {
      return imageTags;
    }
  } else {
    return fallbackTags;
  }
}

function getPreviewVideoTags(previewVideo, siteUrl) {
  const alt = previewVideo.alternativeText || previewVideo.name || "";
  const url = previewVideo.url;
  const type = previewVideo.mime;

  return [
    { property: "og:video", content: getAbsoluteUrl(url, siteUrl) },
    {
      property: "og:video:alt",
      content: alt,
    },
    {
      property: "og:video:type",
      content: type,
    },
  ];
}

function getAbsoluteUrl(url, siteUrl) {
  if (!siteUrl || !url || url.match(/^http/)) {
    return url;
  }
  return `${siteUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

function getTwitterTags(twitter) {
  if (!twitter?.cardType || (!twitter?.handle && !twitter?.site)) {
    return <></>;
  }

  return (
    <>
      <meta name="twitter:card" content={twitter.cardType} />
      {!twitter.site ? (
        <></>
      ) : (
        <meta name="twitter:site" content={twitter.site} />
      )}
      {!twitter.handle ? (
        <></>
      ) : (
        <meta name="twitter:creator" content={twitter.handle} />
      )}
    </>
  );
}

function getFacebookTags(facebook) {
  if (!facebook?.appId) {
    return <></>;
  }

  return (
    <>
      <meta name="fb:app_id" content={facebook.appId} />
    </>
  );
}

function getMetaTags(metaTags) {
  if (!metaTags?.length) {
    return <></>;
  }

  return metaTags.map((metaTag, idx) => {
    return metaTag.isProperty ? (
      <meta
        key={`meta-tag-${idx}`}
        property={metaTag.name}
        content={metaTag.content}
      />
    ) : (
      <meta
        key={`meta-tag-${idx}`}
        name={metaTag.name}
        content={metaTag.content}
      />
    );
  });
}
